import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getScoreTrends,
  getScoreAggregates,
  listEvalRuns,
  triggerEvalRun,
  continueEvalRun,
  listTestCases,
  updateTestCase,
  createTestCase,
  deleteTestCase,
  getEvalRun,
  getExecutionTrace,
  submitHumanReview,
  getFewShotStatus,
  getScorerPrompts,
  getScorerPromptHistory,
  saveScorerPrompt,
  activateScorerPrompt,
  type EvalRun,
  type EvalTestCase,
  type ExecutionTrace,
  type PromptScorerType,
  type MetricName,
  type ScorerPromptHistoryItem,
} from '@/lib/api';
import { Layout } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Loader2,
  Play,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  CheckCircle2,
  XCircle,
  BarChart3,
  FlaskConical,
  FileText,
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Cpu,
  Search,
  Bot,
  Scale,
  History,
  Pencil,
  Save,
  RotateCcw,
  Tag,
  HelpCircle,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LabeledSelect } from '@/components/ui/labeled-select';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

// Helper to parse UTC timestamps from the database
// SQLite stores as '2025-12-17 04:06:15' without timezone, but it's UTC
function parseUTCDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  // If the date string doesn't end with Z, append it to treat as UTC
  const utcStr = dateStr.endsWith('Z') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  return new Date(utcStr);
}

// Feedback categories for human review
const FEEDBACK_CATEGORIES = [
  { value: 'hallucination', label: 'Hallucination', description: 'AI made up information not in sources' },
  { value: 'incomplete', label: 'Incomplete', description: 'Missing important information' },
  { value: 'wrong_source', label: 'Wrong Source', description: 'Cited incorrect or outdated source' },
  { value: 'off_topic', label: 'Off Topic', description: "Doesn't address the question" },
  { value: 'tone', label: 'Tone/Style', description: 'Inappropriate tone or formatting' },
  { value: 'outdated', label: 'Outdated Info', description: 'Information is stale or changed' },
  { value: 'too_verbose', label: 'Too Verbose', description: 'Response is unnecessarily long' },
  { value: 'too_brief', label: 'Too Brief', description: 'Needs more detail' },
  { value: 'other', label: 'Other', description: 'Other reason (specify in notes)' },
] as const;

function TrendIndicator({ current, previous }: { current: number; previous?: number }) {
  if (previous === undefined) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  const diff = current - previous;
  const percentage = previous > 0 ? (diff / previous) * 100 : 0;
  
  if (Math.abs(percentage) < 1) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
  
  if (diff > 0) {
    return (
      <div className="flex items-center text-green-500">
        <TrendingUp className="h-4 w-4 mr-1" />
        <span className="text-xs">+{percentage.toFixed(1)}%</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center text-red-500">
      <TrendingDown className="h-4 w-4 mr-1" />
      <span className="text-xs">{percentage.toFixed(1)}%</span>
    </div>
  );
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  const percentage = Math.round(score * 100);
  
  const getColor = () => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    if (percentage >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground capitalize">{label.replace(/_/g, ' ')}</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn('h-full transition-all', getColor())}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function EvalRunStatus({ status }: { status: EvalRun['status'] }) {
  switch (status) {
    case 'completed':
      return <Badge variant="default" className="bg-green-500">Completed</Badge>;
    case 'running':
      return <Badge variant="default" className="bg-blue-500">Running</Badge>;
    case 'pending':
      return <Badge variant="secondary">Pending</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'cancelled':
      return <Badge variant="outline">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// Ground truth labeling component for test cases
function GroundTruthLabel({
  testCase,
  onUpdate,
  isUpdating,
}: {
  testCase: EvalTestCase;
  onUpdate: (groundTruthPass: boolean | null, reasoning?: string) => void;
  isUpdating: boolean;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedPass, setSelectedPass] = useState<boolean | null>(testCase.ground_truth_pass ?? null);
  const [reasoning, setReasoning] = useState(testCase.ground_truth_reasoning || '');

  const handleSave = () => {
    onUpdate(selectedPass, reasoning || undefined);
    setShowDialog(false);
  };

  // Display current state
  const renderLabel = () => {
    if (testCase.ground_truth_pass === true) {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200 cursor-pointer hover:bg-green-100"
          onClick={() => setShowDialog(true)}
        >
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Should Pass
        </Badge>
      );
    }
    if (testCase.ground_truth_pass === false) {
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200 cursor-pointer hover:bg-red-100"
          onClick={() => setShowDialog(true)}
        >
          <XCircle className="h-3 w-3 mr-1" />
          Should Fail
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-gray-50 text-gray-500 border-gray-200 cursor-pointer hover:bg-gray-100"
        onClick={() => setShowDialog(true)}
      >
        <HelpCircle className="h-3 w-3 mr-1" />
        Unlabeled
      </Badge>
    );
  };

  return (
    <>
      {isUpdating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        renderLabel()
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Label Ground Truth
            </DialogTitle>
            <DialogDescription>
              Set the expected outcome for this test case. This enables TPR/TNR metrics.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Test Case</Label>
              <p className="text-sm text-muted-foreground mt-1">{testCase.name}</p>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{testCase.query}</p>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Expected Outcome</Label>
              <div className="flex gap-2">
                <Button
                  variant={selectedPass === true ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPass(true)}
                  className={selectedPass === true ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Should Pass
                </Button>
                <Button
                  variant={selectedPass === false ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPass(false)}
                  className={selectedPass === false ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Should Fail
                </Button>
                <Button
                  variant={selectedPass === null ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPass(null)}
                >
                  <HelpCircle className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="reasoning" className="text-sm font-medium">
                Reasoning (optional)
              </Label>
              <Textarea
                id="reasoning"
                value={reasoning}
                onChange={(e) => setReasoning(e.target.value)}
                placeholder="Why should this test case pass or fail?"
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-1" />
              Save Label
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Component to show individual test result details with trace view and human review
function TestResultRow({
  result,
  isExpanded,
  onToggle,
  onViewTrace,
  onSubmitReview,
  isSubmittingReview,
  onRerunTestCase,
  isRerunning,
}: {
  result: {
    id: string;
    testCaseId: string;
    executionId?: string;
    query: string;
    expectedResponse?: string;
    actualResponse?: string;
    passed: boolean;
    overallScore?: number;
    scoresDetail?: Record<string, unknown>;
    errorMessage?: string;
    humanReviewed?: boolean;
    humanApproved?: boolean;
    humanReviewNotes?: string;
    // Ground truth comparison
    groundTruthPass?: boolean | null;
    predictedPass?: boolean;
    predictionCorrect?: boolean | null;
    // Whether the test case still exists/is active
    testCaseExists?: boolean;
  };
  isExpanded: boolean;
  onToggle: () => void;
  onViewTrace?: (executionId: string, scores?: Record<string, unknown>) => void;
  onSubmitReview?: (resultId: string, approved: boolean, notes?: string) => void;
  isSubmittingReview?: boolean;
  onRerunTestCase?: (testCaseId: string, query: string) => void;
  isRerunning?: boolean;
}) {
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState<string>('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null);

  const score = result.overallScore ?? 0;
  const scorePercent = Math.round(score * 100);

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingAction('approve');
    setFeedbackCategory('');
    setFeedbackNotes('');
    setShowFeedbackDialog(true);
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingAction('reject');
    setFeedbackCategory('');
    setFeedbackNotes('');
    setShowFeedbackDialog(true);
  };

  const handleSubmitFeedback = () => {
    if (!pendingAction) return;

    // Build notes with category prefix if selected
    let notes = feedbackNotes;
    if (feedbackCategory && feedbackCategory !== 'other') {
      const categoryLabel = FEEDBACK_CATEGORIES.find(c => c.value === feedbackCategory)?.label;
      notes = categoryLabel ? `[${categoryLabel}] ${feedbackNotes}`.trim() : feedbackNotes;
    }

    onSubmitReview?.(result.id, pendingAction === 'approve', notes || undefined);
    setShowFeedbackDialog(false);
    setPendingAction(null);
    setFeedbackCategory('');
    setFeedbackNotes('');
  };

  const handleCancelFeedback = () => {
    setShowFeedbackDialog(false);
    setPendingAction(null);
    setFeedbackCategory('');
    setFeedbackNotes('');
  };

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={onToggle}
      >
        <TableCell className="w-8">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {result.passed ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
            {result.humanReviewed && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {result.humanApproved ? 'Approved' : 'Rejected'}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="font-medium max-w-[400px]">
          <span className="line-clamp-2">{result.query}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className={cn(
            "font-medium",
            scorePercent >= 80 ? "text-green-600" :
            scorePercent >= 60 ? "text-yellow-600" :
            "text-red-600"
          )}>
            {scorePercent}%
          </span>
        </TableCell>
        <TableCell className="text-center">
          {result.groundTruthPass !== null && result.groundTruthPass !== undefined ? (
            result.predictionCorrect === true ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Match
              </Badge>
            ) : result.predictionCorrect === false ? (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <XCircle className="h-3 w-3 mr-1" />
                Mismatch
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">-</span>
            )
          ) : (
            <span className="text-muted-foreground text-xs">No GT</span>
          )}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="bg-muted/30">
          <TableCell colSpan={5} className="p-4">
            <div className="space-y-4">
              {/* Action Buttons */}
              <div className="flex items-center gap-2 pb-2 border-b">
                {result.executionId && onViewTrace && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTrace(result.executionId!, result.scoresDetail);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Trace
                  </Button>
                )}
                {onRerunTestCase && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Re-run clicked, testCaseId:', result.testCaseId, 'full result:', result);
                              if (!result.testCaseId) {
                                console.error('testCaseId is missing from result');
                                return;
                              }
                              onRerunTestCase(result.testCaseId, result.query);
                            }}
                            disabled={isRerunning || !result.testCaseId || result.testCaseExists === false}
                          >
                            {isRerunning ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4 mr-1" />
                            )}
                            Re-run
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {result.testCaseExists === false && (
                        <TooltipContent>
                          <p>Test case has been deleted</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
                <div className="flex-1" />
                <span className="text-xs text-muted-foreground mr-2">Human Review:</span>
                <Button
                  variant={result.humanApproved === true ? "default" : "outline"}
                  size="sm"
                  className={result.humanApproved === true ? "bg-green-600 hover:bg-green-700" : ""}
                  onClick={handleApprove}
                  disabled={isSubmittingReview}
                >
                  <ThumbsUp className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant={result.humanApproved === false ? "default" : "outline"}
                  size="sm"
                  className={result.humanApproved === false ? "bg-red-600 hover:bg-red-700" : ""}
                  onClick={handleReject}
                  disabled={isSubmittingReview}
                >
                  <ThumbsDown className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>

              {/* Human Review Notes (if exists) */}
              {result.humanReviewed && result.humanReviewNotes && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="text-xs font-semibold text-amber-800 dark:text-amber-200 uppercase mb-1">
                    Reviewer Feedback
                  </div>
                  <p className="text-sm text-amber-900 dark:text-amber-100">
                    {result.humanReviewNotes}
                  </p>
                </div>
              )}

              {/* Expected vs Actual Response */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Expected Response</h4>
                  <div className="text-sm bg-background p-3 rounded border max-h-48 overflow-y-auto whitespace-pre-wrap">
                    {result.expectedResponse || 'No expected response defined'}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Actual Response</h4>
                  <div className="text-sm bg-background p-3 rounded border max-h-48 overflow-y-auto whitespace-pre-wrap">
                    {result.actualResponse || 'No response generated'}
                  </div>
                </div>
              </div>

              {/* Score Breakdown */}
              {result.scoresDetail && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Score Breakdown</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {/* Workflow Scores */}
                    {(result.scoresDetail as { workflowScores?: { queryExpander?: { score: number }; retrieval?: { score: number }; formatter?: { score: number } } }).workflowScores && (
                      <>
                        {(result.scoresDetail as { workflowScores: { queryExpander?: { score: number } } }).workflowScores.queryExpander && (
                          <div className="bg-background p-2 rounded border">
                            <div className="text-xs text-muted-foreground">Query Expander</div>
                            <div className="font-medium">{Math.round((result.scoresDetail as { workflowScores: { queryExpander: { score: number } } }).workflowScores.queryExpander.score * 100)}%</div>
                          </div>
                        )}
                        {(result.scoresDetail as { workflowScores: { retrieval?: { score: number } } }).workflowScores.retrieval && (
                          <div className="bg-background p-2 rounded border">
                            <div className="text-xs text-muted-foreground">Retrieval</div>
                            <div className="font-medium">{Math.round((result.scoresDetail as { workflowScores: { retrieval: { score: number } } }).workflowScores.retrieval.score * 100)}%</div>
                          </div>
                        )}
                        {(result.scoresDetail as { workflowScores: { formatter?: { score: number } } }).workflowScores.formatter && (
                          <div className="bg-background p-2 rounded border">
                            <div className="text-xs text-muted-foreground">Formatter</div>
                            <div className="font-medium">{Math.round((result.scoresDetail as { workflowScores: { formatter: { score: number } } }).workflowScores.formatter.score * 100)}%</div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Keyword Score */}
                    {(result.scoresDetail as { keywordScore?: number }).keywordScore !== undefined && (
                      <div className="bg-background p-2 rounded border">
                        <div className="text-xs text-muted-foreground">Keyword Match</div>
                        <div className="font-medium">{Math.round((result.scoresDetail as { keywordScore: number }).keywordScore * 100)}%</div>
                      </div>
                    )}
                  </div>
                  {/* Keywords Found/Missed */}
                  {((result.scoresDetail as { keywordsFound?: string[] }).keywordsFound?.length ?? 0) > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-muted-foreground">Keywords found: </span>
                      <span className="text-xs text-green-600">
                        {(result.scoresDetail as { keywordsFound: string[] }).keywordsFound.join(', ')}
                      </span>
                    </div>
                  )}
                  {((result.scoresDetail as { keywordsMissed?: string[] }).keywordsMissed?.length ?? 0) > 0 && (
                    <div className="mt-1">
                      <span className="text-xs text-muted-foreground">Keywords missed: </span>
                      <span className="text-xs text-red-600">
                        {(result.scoresDetail as { keywordsMissed: string[] }).keywordsMissed.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {result.errorMessage && (
                <div>
                  <h4 className="text-xs font-semibold text-red-600 uppercase mb-2">Error</h4>
                  <div className="text-sm bg-red-50 text-red-800 p-3 rounded border border-red-200">
                    {result.errorMessage}
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>
              {pendingAction === 'approve' ? 'Approve Result' : 'Reject Result'}
            </DialogTitle>
            <DialogDescription>
              {pendingAction === 'approve'
                ? 'Optionally add notes explaining why this result is good.'
                : 'Please provide feedback on why this result is incorrect. This helps improve the AI evaluator.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Category selector (mainly for rejections) */}
            {pendingAction === 'reject' && (
              <div className="space-y-2">
                <Label htmlFor="feedback-category">Rejection Category</Label>
                <LabeledSelect
                  value={feedbackCategory}
                  onValueChange={setFeedbackCategory}
                  options={[...FEEDBACK_CATEGORIES]}
                  placeholder="Select a category..."
                  renderOption={(option) => (
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Notes textarea */}
            <div className="space-y-2">
              <Label htmlFor="feedback-notes">
                {pendingAction === 'approve' ? 'Notes (optional)' : 'Detailed Feedback'}
              </Label>
              <Textarea
                id="feedback-notes"
                placeholder={
                  pendingAction === 'approve'
                    ? 'e.g., "Good response, correctly addressed the question"'
                    : 'e.g., "The response mentions a deadline that doesn\'t exist in the source documents"'
                }
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
                rows={4}
              />
              {pendingAction === 'reject' && (
                <p className="text-xs text-muted-foreground">
                  Be specific about what was wrong. This feedback helps train the AI to make better judgments.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelFeedback}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFeedback}
              disabled={isSubmittingReview || (pendingAction === 'reject' && !feedbackCategory)}
              className={pendingAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {isSubmittingReview ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : pendingAction === 'approve' ? (
                <ThumbsUp className="h-4 w-4 mr-2" />
              ) : (
                <ThumbsDown className="h-4 w-4 mr-2" />
              )}
              {pendingAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Slack-like message renderer - renders markdown similar to Slack
function SlackMessagePreview({ message }: { message: string }) {
  // Parse Slack-style markdown
  const renderSlackMarkdown = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];

    lines.forEach((line, lineIdx) => {
      // Handle code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          // End of code block
          elements.push(
            <pre key={`code-${lineIdx}`} className="bg-slate-800 text-slate-100 p-3 rounded text-xs overflow-x-auto my-2 font-mono">
              <code>{codeBlockContent.join('\n')}</code>
            </pre>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          // Start of code block
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        return;
      }

      // Empty line
      if (!line.trim()) {
        elements.push(<div key={lineIdx} className="h-2" />);
        return;
      }

      // Bullet points
      if (line.match(/^[•\-*]\s/)) {
        const content = line.replace(/^[•\-*]\s/, '');
        elements.push(
          <div key={lineIdx} className="flex items-start gap-2 pl-2">
            <span className="text-muted-foreground">•</span>
            <span>{renderInlineMarkdown(content)}</span>
          </div>
        );
        return;
      }

      // Regular line with inline formatting
      elements.push(
        <p key={lineIdx} className="leading-relaxed">
          {renderInlineMarkdown(line)}
        </p>
      );
    });

    return elements;
  };

  // Render inline markdown (bold, italic, code, links)
  const renderInlineMarkdown = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      // Bold: *text* or **text**
      const boldMatch = remaining.match(/^\*\*?([^*]+)\*\*?/);
      if (boldMatch) {
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Inline code: `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        parts.push(
          <code key={key++} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-pink-600">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // Links: <url> or [text](url)
      const linkMatch = remaining.match(/^<(https?:\/\/[^>]+)>/) || remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        const url = linkMatch[2] || linkMatch[1];
        const linkText = linkMatch[2] ? linkMatch[1] : linkMatch[1];
        parts.push(
          <a key={key++} href={url} className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        );
        remaining = remaining.slice(linkMatch[0].length);
        continue;
      }

      // Plain text (one character at a time until we hit special syntax)
      const nextSpecial = remaining.search(/[*`<[]/)
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        // Special char that didn't match a pattern, treat as plain
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        parts.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
    }

    return <>{parts}</>;
  };

  return (
    <div className="bg-white dark:bg-slate-900 border rounded-lg p-4 text-sm space-y-1 max-h-64 overflow-y-auto shadow-sm">
      {/* Slack-style header */}
      <div className="flex items-center gap-2 pb-2 mb-2 border-b">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
          AI
        </div>
        <div>
          <div className="font-semibold text-sm">Atlas AI</div>
          <div className="text-xs text-muted-foreground">Bot</div>
        </div>
      </div>
      {/* Message content */}
      <div className="text-foreground">
        {renderSlackMarkdown(message)}
      </div>
    </div>
  );
}

// Helper to format output nicely
function formatOutput(output: unknown, key: string): React.ReactNode {
  if (typeof output === 'string') {
    return <p className="whitespace-pre-wrap">{output}</p>;
  }

  // Special formatting for different phases
  if (key === 'queryExpander' && typeof output === 'object' && output) {
    const o = output as { enrichedQuery?: string; searchTerms?: string[]; detectedAbbreviations?: Array<{ abbrev: string; full: string }> };
    return (
      <div className="space-y-2">
        {o.enrichedQuery && (
          <div>
            <span className="text-muted-foreground text-xs">Enriched Query:</span>
            <p className="font-medium">{o.enrichedQuery}</p>
          </div>
        )}
        {o.searchTerms && o.searchTerms.length > 0 && (
          <div>
            <span className="text-muted-foreground text-xs">Search Terms:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {o.searchTerms.map((term, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{term}</Badge>
              ))}
            </div>
          </div>
        )}
        {o.detectedAbbreviations && o.detectedAbbreviations.length > 0 && (
          <div>
            <span className="text-muted-foreground text-xs">Abbreviations Detected:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {o.detectedAbbreviations.map((a, i) => (
                <Badge key={i} variant="outline" className="text-xs">{a.abbrev} → {a.full}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (key === 'retrieval' && typeof output === 'object' && output) {
    const o = output as { totalResults?: number; topScore?: number; resultsPreview?: Array<{ title?: string; source?: string; score?: number }> };
    return (
      <div className="space-y-2">
        <div className="flex gap-4">
          <div>
            <span className="text-muted-foreground text-xs">Results Found:</span>
            <p className="font-medium">{o.totalResults ?? 0}</p>
          </div>
          <div>
            <span className="text-muted-foreground text-xs">Top Score:</span>
            <p className="font-medium">{o.topScore ? `${(o.topScore * 100).toFixed(1)}%` : '-'}</p>
          </div>
        </div>
        {o.resultsPreview && o.resultsPreview.length > 0 && (
          <div>
            <span className="text-muted-foreground text-xs">Top Results:</span>
            <div className="space-y-1 mt-1">
              {o.resultsPreview.slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-muted/50 p-1.5 rounded">
                  <Badge variant="outline" className="text-[10px]">{r.source}</Badge>
                  <span className="flex-1 truncate">{r.title || 'Untitled'}</span>
                  <span className="text-muted-foreground">{r.score ? `${(r.score * 100).toFixed(0)}%` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (key === 'assessment' && typeof output === 'object' && output) {
    const o = output as { hasResults?: boolean; isRelevantTopic?: boolean; responseType?: string };
    return (
      <div className="flex gap-4">
        <div className="flex items-center gap-1">
          {o.hasResults ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
          <span className="text-xs">Has Results</span>
        </div>
        <div className="flex items-center gap-1">
          {o.isRelevantTopic ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
          <span className="text-xs">Relevant Topic</span>
        </div>
        <Badge variant="outline">{o.responseType || 'unknown'}</Badge>
      </div>
    );
  }

  if (key === 'reasoning' && typeof output === 'object' && output) {
    const o = output as { thinking?: string; answer?: string };
    return (
      <div className="space-y-3">
        {o.thinking && (
          <div>
            <span className="text-muted-foreground text-xs flex items-center gap-1">
              <Cpu className="h-3 w-3" /> Model Thinking:
            </span>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-2 rounded mt-1 text-sm italic">
              {o.thinking}
            </div>
          </div>
        )}
        {o.answer && (
          <div>
            <span className="text-muted-foreground text-xs">Raw Answer:</span>
            <div className="bg-muted p-2 rounded mt-1 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
              {o.answer}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (key === 'formatting' && typeof output === 'object' && output) {
    const o = output as { formattedResponse?: string; fallbackUsed?: boolean };
    return (
      <div className="space-y-2">
        {o.fallbackUsed && (
          <Badge variant="destructive" className="text-xs">Fallback Used</Badge>
        )}
        {o.formattedResponse && (
          <SlackMessagePreview message={o.formattedResponse} />
        )}
      </div>
    );
  }

  // Default: JSON
  return (
    <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32 overflow-y-auto">
      {JSON.stringify(output, null, 2)}
    </pre>
  );
}

// Context item type for reasoning input
interface ContextItem {
  id: string;
  title: string;
  url?: string;
  source: string;
  score: number;
  contentPreview?: string;
  content?: string;
}

// Component to render reasoning input with expandable context
function ReasoningContextInput({ input }: { input: { query: string; context?: ContextItem[] } }) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showAllItems, setShowAllItems] = useState(false);

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const context = input.context || [];
  const displayedContext = showAllItems ? context : context.slice(0, 5);

  return (
    <div className="space-y-3">
      <div>
        <span className="text-muted-foreground text-xs">Query:</span>
        <p className="font-medium text-sm mt-1">{input.query}</p>
      </div>

      {context.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground text-xs">
              Retrieved Context ({context.length} items):
            </span>
            {context.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setShowAllItems(!showAllItems)}
              >
                {showAllItems ? 'Show less' : `Show all ${context.length}`}
              </Button>
            )}
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {displayedContext.map((item, i) => {
              const isExpanded = expandedItems.has(item.id);
              const hasFullContent = item.content && item.content.length > 0;
              const displayContent = isExpanded ? item.content : item.contentPreview;

              return (
                <div key={item.id || i} className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 p-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{item.source}</Badge>
                    <span className="flex-1 text-xs font-medium truncate">{item.title}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {(item.score * 100).toFixed(0)}%
                    </span>
                    {hasFullContent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-2 text-[10px]"
                        onClick={() => toggleItem(item.id)}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Collapse
                          </>
                        ) : (
                          <>
                            <Eye className="h-3 w-3 mr-1" />
                            Expand
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  {displayContent && (
                    <div className={cn(
                      "p-2 text-xs bg-background",
                      isExpanded ? "max-h-[300px] overflow-y-auto" : "max-h-[60px] overflow-hidden"
                    )}>
                      <p className="whitespace-pre-wrap text-muted-foreground">
                        {displayContent}
                        {!isExpanded && item.content && item.content.length > 200 && (
                          <span className="text-blue-500 ml-1">...</span>
                        )}
                      </p>
                    </div>
                  )}
                  {item.url && isExpanded && (
                    <div className="px-2 pb-2 text-[10px] text-muted-foreground">
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {item.url}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Component to display execution trace in a modal with scores
function TraceViewer({
  trace,
  scores,
  onClose
}: {
  trace: ExecutionTrace | null;
  scores?: Record<string, unknown>;
  onClose: () => void;
}) {
  const [expandedPhase, setExpandedPhase] = useState<string | null>(null);

  if (!trace) return null;

  // Extract scores from the scores object (includes reasoning from LLM judges)
  const workflowScores = (scores as {
    workflowScores?: Record<string, {
      score: number;
      details?: Record<string, number>;
      reasoning?: string;
      judgeType?: string;
      judgeModel?: string;
    }>
  })?.workflowScores;

  // Helper to safely extract duration from a phase
  const getDuration = (phaseData: unknown): number | undefined => {
    if (typeof phaseData === 'object' && phaseData !== null && 'durationMs' in phaseData) {
      return (phaseData as { durationMs: number }).durationMs;
    }
    return undefined;
  };

  // Helper to safely extract model from a phase
  const getModel = (phaseData: unknown): string | undefined => {
    if (typeof phaseData === 'object' && phaseData !== null && 'model' in phaseData) {
      return (phaseData as { model: string }).model;
    }
    // Check for embedding model in retrieval
    if (typeof phaseData === 'object' && phaseData !== null && 'embeddingModel' in phaseData) {
      return (phaseData as { embeddingModel: string }).embeddingModel;
    }
    return undefined;
  };

  const phases = [
    {
      key: 'queryExpander',
      name: 'Query Expander',
      description: 'Enriches and expands the user query',
      icon: Search,
      data: trace.phases.queryExpander,
      durationMs: getDuration(trace.phases.queryExpander),
      model: getModel(trace.phases.queryExpander),
      score: workflowScores?.queryExpander?.score,
      scoreDetails: workflowScores?.queryExpander?.details,
      reasoning: workflowScores?.queryExpander?.reasoning,
      judgeType: workflowScores?.queryExpander?.judgeType,
    },
    {
      key: 'retrieval',
      name: 'Retrieval',
      description: 'Searches knowledge base for relevant documents',
      icon: Search,
      data: trace.phases.retrieval,
      durationMs: getDuration(trace.phases.retrieval),
      model: getModel(trace.phases.retrieval),
      score: workflowScores?.retrieval?.score,
      scoreDetails: workflowScores?.retrieval?.details,
      reasoning: workflowScores?.retrieval?.reasoning,
      judgeType: workflowScores?.retrieval?.judgeType,
    },
    {
      key: 'assessment',
      name: 'Assessment',
      description: 'Evaluates if results are relevant',
      icon: Cpu,
      data: trace.phases.assessment,
      durationMs: getDuration(trace.phases.assessment),
      model: getModel(trace.phases.assessment),
      score: null, // Assessment is deterministic, no LLM score
      scoreDetails: null,
      reasoning: undefined,
      judgeType: undefined,
    },
    {
      key: 'reasoning',
      name: 'Reasoning',
      description: 'Synthesizes answer from retrieved context',
      icon: Cpu,
      data: trace.phases.reasoning,
      durationMs: getDuration(trace.phases.reasoning),
      model: getModel(trace.phases.reasoning),
      score: workflowScores?.reasoner?.score,
      scoreDetails: workflowScores?.reasoner?.details,
      reasoning: workflowScores?.reasoner?.reasoning,
      judgeType: workflowScores?.reasoner?.judgeType,
    },
    {
      key: 'formatting',
      name: 'Formatting',
      description: 'Formats response for Slack',
      icon: FileText,
      data: trace.phases.formatting,
      durationMs: getDuration(trace.phases.formatting),
      model: getModel(trace.phases.formatting),
      score: workflowScores?.formatter?.score,
      scoreDetails: workflowScores?.formatter?.details,
      reasoning: workflowScores?.formatter?.reasoning,
      judgeType: workflowScores?.formatter?.judgeType,
    },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100 dark:bg-green-900/30';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30';
  };

  return (
    <Dialog open={!!trace} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Execution Trace
            <Badge variant="outline" className="font-normal">
              {trace.totalDurationMs ?? '-'}ms total
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Step-by-step breakdown of the RAG workflow
          </DialogDescription>
        </DialogHeader>

        {/* Pipeline Overview - Visual Timeline */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg mb-4 overflow-x-auto">
          {phases.map((phase, i) => (
            <div key={phase.key} className="flex items-center">
              <button
                onClick={() => setExpandedPhase(expandedPhase === phase.key ? null : phase.key)}
                className={cn(
                  "flex flex-col items-center p-2 rounded-lg transition-colors min-w-[80px]",
                  expandedPhase === phase.key ? "bg-background shadow-sm" : "hover:bg-background/50"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-1",
                  phase.score !== null && phase.score !== undefined
                    ? getScoreColor(phase.score)
                    : "bg-muted-foreground/20"
                )}>
                  <phase.icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-center">{phase.name}</span>
                {phase.score !== null && phase.score !== undefined && (
                  <span className={cn("text-xs font-bold", getScoreColor(phase.score).split(' ')[0])}>
                    {Math.round(phase.score * 100)}%
                  </span>
                )}
                {phase.durationMs !== undefined && phase.durationMs !== null && phase.durationMs > 0 ? (
                  <span className="text-[10px] text-muted-foreground">{phase.durationMs}ms</span>
                ) : (
                  <span className="text-[10px] text-muted-foreground/60">{phase.durationMs === 0 ? '<1ms' : 'Skipped'}</span>
                )}
              </button>
              {i < phases.length - 1 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground mx-1 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>

        {/* Expanded Phase Details */}
        {expandedPhase && (
          <div className="border rounded-lg p-4 mb-4 animate-in fade-in slide-in-from-top-2">
            {(() => {
              const phase = phases.find(p => p.key === expandedPhase);
              if (!phase) return null;

              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <phase.icon className="h-4 w-4" />
                        {phase.name}
                      </h3>
                      <p className="text-xs text-muted-foreground">{phase.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {phase.model && (
                        <Badge variant="outline">{phase.model}</Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {phase.durationMs !== undefined && phase.durationMs !== null && phase.durationMs > 0
                          ? `${phase.durationMs}ms`
                          : phase.durationMs === 0 ? '<1ms' : 'Skipped'}
                      </Badge>
                      {phase.score !== null && phase.score !== undefined && (
                        <Badge className={cn("text-sm", getScoreColor(phase.score))}>
                          Score: {Math.round(phase.score * 100)}%
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Score Details */}
                  {phase.scoreDetails && Object.keys(phase.scoreDetails).length > 0 && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                        Score Breakdown
                        {phase.judgeType === 'llm' && (
                          <span className="ml-2 text-blue-600 font-normal">(AI Judge)</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(phase.scoreDetails).map(([key, value]) => (
                          <div key={key} className="text-center p-2 bg-background rounded">
                            <div className="text-xs text-muted-foreground capitalize">
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className={cn(
                              "font-bold",
                              (value as number) >= 0.8 ? "text-green-600" :
                              (value as number) >= 0.6 ? "text-yellow-600" : "text-red-600"
                            )}>
                              {Math.round((value as number) * 100)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Judge Reasoning */}
                  {phase.reasoning && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase mb-2">
                        AI Judge Reasoning
                      </div>
                      <div className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                        {phase.reasoning}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input</div>
                      <div className="bg-muted p-3 rounded min-h-[100px] max-h-[400px] overflow-y-auto">
                        {/* Special rendering for reasoning phase with expandable context */}
                        {phase.key === 'reasoning' && typeof phase.data.input === 'object' && phase.data.input && 'context' in phase.data.input ? (
                          <ReasoningContextInput input={phase.data.input as { query: string; context?: ContextItem[] }} />
                        ) : typeof phase.data.input === 'string' ? (
                          <p className="text-sm">{phase.data.input}</p>
                        ) : (
                          <pre className="text-xs whitespace-pre-wrap">
                            {JSON.stringify(phase.data.input, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Output</div>
                      <div className="bg-muted p-3 rounded min-h-[100px] max-h-[200px] overflow-y-auto text-sm">
                        {formatOutput(phase.data.output, phase.key)}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div className="bg-muted p-2 rounded text-center">
            <div className="text-xs text-muted-foreground">Duration</div>
            <div className="font-medium">{trace.totalDurationMs ?? '-'}ms</div>
          </div>
          <div className="bg-muted p-2 rounded text-center">
            <div className="text-xs text-muted-foreground">Input Tokens</div>
            <div className="font-medium">{trace.totalInputTokens ?? '-'}</div>
          </div>
          <div className="bg-muted p-2 rounded text-center">
            <div className="text-xs text-muted-foreground">Output Tokens</div>
            <div className="font-medium">{trace.totalOutputTokens ?? '-'}</div>
          </div>
          <div className="bg-muted p-2 rounded text-center">
            <div className="text-xs text-muted-foreground">Est. Cost</div>
            <div className="font-medium">${(trace.estimatedCostUsd ?? 0).toFixed(4)}</div>
          </div>
        </div>

        {/* Hint */}
        <p className="text-xs text-muted-foreground text-center mt-2">
          Click on any phase above to see detailed inputs, outputs, and score breakdown
        </p>
      </DialogContent>
    </Dialog>
  );
}

// Prompt Editor Dialog
function PromptEditorDialog({
  open,
  onOpenChange,
  scorerType,
  metricName,
  currentPrompt,
  currentVersion,
  displayName,
  description,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scorerType: PromptScorerType;
  metricName: MetricName;
  currentPrompt: string;
  currentVersion: number;
  displayName: string;
  description: string;
}) {
  const queryClient = useQueryClient();
  const [editedPrompt, setEditedPrompt] = useState(currentPrompt);
  const [notes, setNotes] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setEditedPrompt(currentPrompt);
      setNotes('');
      setShowHistory(false);
    }
  }, [open, currentPrompt]);

  const hasChanges = editedPrompt !== currentPrompt;

  // Fetch version history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['scorer-prompt-history', scorerType, metricName],
    queryFn: () => getScorerPromptHistory(scorerType, metricName),
    enabled: showHistory,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () =>
      saveScorerPrompt({
        scorerType,
        metricName,
        promptTemplate: editedPrompt,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scorer-prompts'] });
      queryClient.invalidateQueries({ queryKey: ['scorer-prompt-history', scorerType, metricName] });
      onOpenChange(false);
    },
  });

  // Activate (rollback) mutation
  const activateMutation = useMutation({
    mutationFn: (promptId: string) => activateScorerPrompt(promptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scorer-prompts'] });
      queryClient.invalidateQueries({ queryKey: ['scorer-prompt-history', scorerType, metricName] });
    },
  });

  const handleLoadVersion = (item: ScorerPromptHistoryItem) => {
    setEditedPrompt(item.promptTemplate);
    setShowHistory(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Edit {displayName} Prompt
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 space-y-4 overflow-y-auto">
          {/* Prompt Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Prompt Template
                <Badge variant="outline" className="ml-2">v{currentVersion}</Badge>
                {hasChanges && <Badge variant="secondary" className="ml-2">Modified</Badge>}
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className="gap-1"
              >
                <History className="h-4 w-4" />
                {showHistory ? 'Hide History' : 'Version History'}
              </Button>
            </div>

            {showHistory ? (
              <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {historyData?.history.map((item) => (
                      <div
                        key={item.id}
                        className={cn(
                          'flex items-center justify-between p-2 rounded border',
                          item.isActive && 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        )}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">v{item.version}</span>
                            {item.isActive && (
                              <Badge className="bg-green-500 text-white text-xs">Active</Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(item.createdAt).toLocaleDateString()} by{' '}
                            {item.createdBy.displayName || item.createdBy.username || 'System'}
                          </div>
                          {item.notes && (
                            <div className="text-xs text-muted-foreground italic mt-1">
                              {item.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLoadVersion(item)}
                          >
                            Load
                          </Button>
                          {!item.isActive && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => activateMutation.mutate(item.id)}
                              disabled={activateMutation.isPending}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Rollback
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="font-mono text-xs min-h-[300px]"
                placeholder="Enter prompt template..."
              />
            )}
          </div>

          {/* Change Notes */}
          {hasChanges && !showHistory && (
            <div className="space-y-2">
              <Label className="text-sm">Change Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe what you changed..."
                className="h-20"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save New Version
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// LLM Judges Section with dynamic prompts
function LLMJudgesSection() {
  const [editingPrompt, setEditingPrompt] = useState<{
    scorerType: PromptScorerType;
    metricName: MetricName;
    prompt: string;
    version: number;
  } | null>(null);

  const { data: promptsData, isLoading } = useQuery({
    queryKey: ['scorer-prompts'],
    queryFn: getScorerPrompts,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Bot className="h-4 w-4" />
            LLM Judges
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const prompts = promptsData?.prompts;
  const metadata = promptsData?.metadata;

  const renderPromptCard = (
    scorerType: PromptScorerType,
    metricName: MetricName,
    weight: string
  ) => {
    const promptData = scorerType === 'reasoner'
      ? prompts?.reasoner[metricName as keyof typeof prompts.reasoner]
      : prompts?.end_to_end[metricName as keyof typeof prompts.end_to_end];

    const displayName = metadata?.displayNames[metricName] || metricName;

    return (
      <div key={metricName} className="bg-muted p-3 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs text-muted-foreground">{displayName}</div>
            <div className="font-bold text-purple-600">{weight}</div>
          </div>
          <div className="flex items-center gap-1">
            {promptData && (
              <Badge variant="outline" className="text-xs">v{promptData.version}</Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() =>
                setEditingPrompt({
                  scorerType,
                  metricName,
                  prompt: promptData?.prompt || '',
                  version: promptData?.version || 0,
                })
              }
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        </div>
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            View prompt
          </summary>
          <div className="mt-2 bg-slate-900 text-slate-100 p-2 rounded text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
            {promptData?.prompt || 'No prompt configured'}
          </div>
        </details>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Bot className="h-4 w-4" />
            LLM Judges
            {promptsData?.fromDefaults && (
              <Badge variant="secondary" className="text-xs">Using Defaults</Badge>
            )}
          </CardTitle>
          <CardDescription>
            GPT-4o-mini evaluations with versioned, editable prompts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Reasoner Scorer */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Reasoner Scorer (Phase 4a)</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Evaluates answer generation quality
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {renderPromptCard('reasoner', 'faithfulness', '40%')}
              {renderPromptCard('reasoner', 'hallucination', '35%')}
              {renderPromptCard('reasoner', 'completeness', '25%')}
            </div>
          </div>

          {/* End-to-End Scorer */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">End-to-End Scorer</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Evaluates overall response quality
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {renderPromptCard('end_to_end', 'answer_relevancy', '35-40%')}
              {renderPromptCard('end_to_end', 'user_satisfaction', '35-40%')}
              {renderPromptCard('end_to_end', 'keyword_coverage', '20%*')}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              * Keyword coverage only applies if keywords are defined in test case
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Prompt Editor Dialog */}
      {editingPrompt && metadata && (
        <PromptEditorDialog
          open={!!editingPrompt}
          onOpenChange={(open) => !open && setEditingPrompt(null)}
          scorerType={editingPrompt.scorerType}
          metricName={editingPrompt.metricName}
          currentPrompt={editingPrompt.prompt}
          currentVersion={editingPrompt.version}
          displayName={metadata.displayNames[editingPrompt.metricName]}
          description={metadata.descriptions[editingPrompt.metricName]}
        />
      )}
    </>
  );
}

// Component to display AI training status from human reviews
function AITrainingStatus() {
  const { data: status, isLoading, error } = useQuery({
    queryKey: ['few-shot-status'],
    queryFn: () => getFewShotStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            AI Training Status
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error || !status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            AI Training Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Unable to load training status. Make sure you have human reviews submitted.
          </p>
        </CardContent>
      </Card>
    );
  }

  const isReady = status.status === 'ready';
  const progressPercent = Math.min(100, Math.round((status.totalReviews / status.minimumRequired) * 100));
  const reviewsNeeded = Math.max(0, status.minimumRequired - status.totalReviews);

  return (
    <Card className={isReady ? 'border-green-200 dark:border-green-800' : 'border-amber-200 dark:border-amber-800'}>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          AI Training Status
          {isReady ? (
            <Badge className="bg-green-500 text-white">Learning Active</Badge>
          ) : (
            <Badge variant="secondary">Collecting Examples</Badge>
          )}
        </CardTitle>
        <CardDescription>
          {isReady
            ? 'AI evaluators are learning from your review patterns'
            : 'Review more results to help AI learn your evaluation criteria'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Training Progress</span>
            <span className="font-medium">{status.totalReviews} / {status.minimumRequired} reviews</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all',
                isReady ? 'bg-green-500' : 'bg-amber-500'
              )}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {!isReady && (
            <p className="text-xs text-muted-foreground">
              {reviewsNeeded} more review{reviewsNeeded !== 1 ? 's' : ''} needed to activate AI learning
            </p>
          )}
        </div>

        {/* Review Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted p-3 rounded-lg text-center">
            <div className="text-2xl font-bold">{status.totalReviews}</div>
            <div className="text-xs text-muted-foreground">Total Reviews</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{status.approvedCount}</div>
            <div className="text-xs text-green-600/80">Approved</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-600">{status.rejectedCount}</div>
            <div className="text-xs text-red-600/80">Rejected</div>
          </div>
        </div>

        {/* Category Breakdown */}
        {Object.keys(status.categoryBreakdown).length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
              Feedback by Category
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(status.categoryBreakdown).map(([category, counts]) => (
                <div key={category} className="bg-muted/50 p-2 rounded text-center">
                  <div className="text-xs font-medium capitalize mb-1">
                    {category.replace(/_/g, ' ')}
                  </div>
                  <div className="flex justify-center gap-2 text-xs">
                    <span className="text-green-600">{counts.approved} ✓</span>
                    <span className="text-red-600">{counts.rejected} ✗</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How it works */}
        {isReady && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mt-4">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm text-green-800 dark:text-green-200">
                  AI Learning is Active
                </h4>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  Your reviews are now being used as examples to teach the AI evaluators.
                  They'll learn to match your approval/rejection patterns over time.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function EvalsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [selectedTrace, setSelectedTrace] = useState<ExecutionTrace | null>(null);
  const [traceScores, setTraceScores] = useState<Record<string, unknown> | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [evalRunsPage, setEvalRunsPage] = useState(0);
  const EVAL_RUNS_PAGE_SIZE = 10;
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTestCase, setNewTestCase] = useState({
    name: '',
    query: '',
    description: '',
    category: '',
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    expectedResponse: '',
    minScoreThreshold: 0.7,
  });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<EvalTestCase | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    query: '',
    description: '',
    category: '',
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    expectedResponse: '',
  });
  const queryClient = useQueryClient();

  // Fetch data
  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['score-trends'],
    queryFn: () => getScoreTrends(30),
  });

  const { data: aggregates, isLoading: aggregatesLoading } = useQuery({
    queryKey: ['score-aggregates'],
    queryFn: () => getScoreAggregates(30),
  });

  const { data: evalRuns, isLoading: runsLoading, isFetching: runsFetching, refetch: refetchRuns } = useQuery({
    queryKey: ['eval-runs', evalRunsPage],
    queryFn: () => listEvalRuns(EVAL_RUNS_PAGE_SIZE, evalRunsPage * EVAL_RUNS_PAGE_SIZE),
    // Poll every 1.5 seconds when an eval is running for more responsive progress updates
    refetchInterval: (query) => {
      const runs = query.state.data?.runs;
      const hasRunning = runs?.some(run => run.status === 'running') ?? false;
      return hasRunning ? 1500 : false;
    },
  });

  const evalRunsTotalPages = evalRuns?.total ? Math.ceil(evalRuns.total / EVAL_RUNS_PAGE_SIZE) : 0;

  const { data: testCases, isLoading: testCasesLoading } = useQuery({
    queryKey: ['test-cases'],
    queryFn: () => listTestCases({ limit: 50, activeOnly: true }),
  });

  // Fetch selected run details
  const { data: selectedRunData, isLoading: selectedRunLoading } = useQuery({
    queryKey: ['eval-run', selectedRunId],
    queryFn: () => selectedRunId ? getEvalRun(selectedRunId) : null,
    enabled: !!selectedRunId,
  });

  // Toggle result expansion
  const toggleResultExpanded = (resultId: string) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(resultId)) {
        next.delete(resultId);
      } else {
        next.add(resultId);
      }
      return next;
    });
  };

  // View trace handler
  const handleViewTrace = async (executionId: string, scores?: Record<string, unknown>) => {
    setTraceLoading(true);
    try {
      const { execution } = await getExecutionTrace(executionId);
      setSelectedTrace(execution);
      setTraceScores(scores || null);
    } catch (err) {
      console.error('Failed to fetch trace:', err);
    } finally {
      setTraceLoading(false);
    }
  };

  // Human review mutation
  const humanReviewMutation = useMutation({
    mutationFn: ({ resultId, approved, notes }: { resultId: string; approved: boolean; notes?: string }) =>
      submitHumanReview(resultId, approved, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-run', selectedRunId] });
      queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
  });

  // Update test case mutation (for ground truth labeling)
  const updateTestCaseMutation = useMutation({
    mutationFn: ({ id, groundTruthPass, groundTruthReasoning }: {
      id: string;
      groundTruthPass: boolean | null;
      groundTruthReasoning?: string;
    }) => updateTestCase(id, { groundTruthPass, groundTruthReasoning }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    },
  });

  // Create test case mutation
  const createTestCaseMutation = useMutation({
    mutationFn: (data: {
      name: string;
      query: string;
      description?: string;
      category?: string;
      difficulty?: 'easy' | 'medium' | 'hard';
      expectedResponse?: string;
      minScoreThreshold?: number;
    }) => createTestCase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-cases'] });
      setShowCreateDialog(false);
      setNewTestCase({
        name: '',
        query: '',
        description: '',
        category: '',
        difficulty: 'medium',
        expectedResponse: '',
        minScoreThreshold: 0.7,
      });
    },
  });

  // Delete test case mutation
  const deleteTestCaseMutation = useMutation({
    mutationFn: (id: string) => deleteTestCase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    },
  });

  // Edit test case mutation
  const editTestCaseMutation = useMutation({
    mutationFn: ({ id, ...data }: {
      id: string;
      name?: string;
      query?: string;
      description?: string;
      category?: string;
      difficulty?: 'easy' | 'medium' | 'hard';
      expectedResponse?: string;
    }) => updateTestCase(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-cases'] });
      setShowEditDialog(false);
      setEditingTestCase(null);
    },
  });

  // Re-run single test case mutation
  const rerunTestCaseMutation = useMutation({
    mutationFn: ({ testCaseId, query }: { testCaseId: string; query: string }) =>
      triggerEvalRun(`Re-run: ${query.substring(0, 50)}${query.length > 50 ? '...' : ''}`, [testCaseId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
  });

  // Trigger eval mutation
  const triggerMutation = useMutation({
    mutationFn: () => triggerEvalRun(`Manual Run ${new Date().toLocaleString()}`),
    onSuccess: async () => {
      // Immediately refetch to show the new run
      await refetchRuns();

      // Also invalidate for subsequent updates
      queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
  });

  // Auto-continue running evals to process next batch
  const processingRunsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const runningEvals = evalRuns?.runs?.filter(run => run.status === 'running') ?? [];

    if (runningEvals.length === 0) return;

    // Call continue endpoint for each running eval
    const continueRunningEvals = async () => {
      // Filter out runs that are already being processed (prevent race condition)
      const runsToProcess = runningEvals.filter(run => !processingRunsRef.current.has(run.id));

      if (runsToProcess.length === 0) return;

      // Mark runs as being processed
      runsToProcess.forEach(run => processingRunsRef.current.add(run.id));

      try {
        // Process all runs concurrently
        await Promise.all(
          runsToProcess.map(async (run) => {
            try {
              await continueEvalRun(run.id);
            } catch (err) {
              console.error(`Failed to continue eval run ${run.id}:`, err);
            }
          })
        );
      } finally {
        // Clear processing state
        runsToProcess.forEach(run => processingRunsRef.current.delete(run.id));

        // Refetch to get updated status (AFTER all calls complete)
        queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
      }
    };

    // Small delay to avoid hammering the API immediately after initial fetch
    const timer = setTimeout(continueRunningEvals, 500);

    return () => clearTimeout(timer);
  }, [evalRuns?.runs, queryClient]);

  // Calculate summary stats from trends
  const todayScore = trends?.trends?.[0]?.avgScore;
  const yesterdayScore = trends?.trends?.[1]?.avgScore;
  const weekAgoScore = trends?.trends?.[6]?.avgScore;
  const totalEvaluations = trends?.trends?.reduce((sum, t) => sum + t.count, 0) ?? 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Evaluations</h1>
            <p className="text-muted-foreground">
              Monitor scoring trends and run evaluation tests
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchRuns()}
              disabled={runsLoading}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', runsLoading && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => triggerMutation.mutate()}
              disabled={triggerMutation.isPending}
            >
              {triggerMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Evaluation
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Today's Avg Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">
                  {todayScore ? `${Math.round(todayScore * 100)}%` : '-'}
                </span>
                <TrendIndicator current={todayScore || 0} previous={yesterdayScore} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                7-Day Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">
                  {todayScore && weekAgoScore 
                    ? `${((todayScore - weekAgoScore) * 100).toFixed(1)}%` 
                    : '-'}
                </span>
                <TrendIndicator current={todayScore || 0} previous={weekAgoScore} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Evaluations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{totalEvaluations.toLocaleString()}</span>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Test Cases
              </CardTitle>
            </CardHeader>
            <CardContent>
              <span className="text-2xl font-bold">{testCases?.count ?? 0}</span>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="runs" className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" />
              Eval Runs
            </TabsTrigger>
            <TabsTrigger value="test-cases" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Test Cases
            </TabsTrigger>
            <TabsTrigger value="ai-evaluators" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              AI Evaluators
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Scores by Stage */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Scores by Stage</CardTitle>
                  <CardDescription>Average scores per workflow stage (last 30 days)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {aggregatesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : aggregates?.byStage ? (
                    Object.entries(aggregates.byStage).map(([stage, score]) => (
                      <ScoreBar key={stage} label={stage} score={score || 0} />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No score data available
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Scores by Judge */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Scores by Judge Type</CardTitle>
                  <CardDescription>Average scores by evaluation method</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {aggregatesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : aggregates?.byJudge ? (
                    Object.entries(aggregates.byJudge).map(([judge, score]) => (
                      <ScoreBar key={judge} label={judge} score={score || 0} />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No score data available
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Score Trend (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {trendsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : trends?.trends && trends.trends.length > 0 ? (
                  <div className="space-y-2">
                    {trends.trends.slice(0, 14).map((trend) => (
                      <div key={trend.date} className="flex items-center gap-4">
                        <span className="text-xs text-muted-foreground w-24">
                          {new Date(trend.date).toLocaleDateString()}
                        </span>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all"
                            style={{ width: `${Math.round(trend.avgScore * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium w-12 text-right">
                          {Math.round(trend.avgScore * 100)}%
                        </span>
                        <span className="text-xs text-muted-foreground w-16 text-right">
                          ({trend.count})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No trend data available
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Eval Runs Tab */}
          <TabsContent value="runs">
            {/* Detail View for Selected Run */}
            {selectedRunId && (
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedRunId(null);
                        setExpandedResults(new Set());
                      }}
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to Runs
                    </Button>
                  </div>
                  {selectedRunData?.run && (
                    <div className="mt-2">
                      <CardTitle className="text-lg">{selectedRunData.run.name || 'Eval Run'}</CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-1">
                        <EvalRunStatus status={selectedRunData.run.status} />
                        <span>{selectedRunData.run.testCount} tests</span>
                        <span className="text-green-600">{selectedRunData.run.passedCount} passed</span>
                        <span className="text-red-600">{selectedRunData.run.failedCount} failed</span>
                        <span>Avg: {selectedRunData.run.avgScore ? `${Math.round(selectedRunData.run.avgScore * 100)}%` : '-'}</span>
                      </CardDescription>

                      {/* TPR/TNR Metrics */}
                      {selectedRunData.run.tprTnr && (selectedRunData.run.tprTnr.tpr !== null || selectedRunData.run.tprTnr.tnr !== null) && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-4">
                              <div>
                                <span className="text-xs text-muted-foreground">TPR</span>
                                <div className={cn(
                                  'text-lg font-semibold',
                                  selectedRunData.run.tprTnr.tpr !== null && selectedRunData.run.tprTnr.tpr >= 0.9 ? 'text-green-600' :
                                  selectedRunData.run.tprTnr.tpr !== null && selectedRunData.run.tprTnr.tpr >= 0.7 ? 'text-yellow-600' : 'text-red-600'
                                )}>
                                  {selectedRunData.run.tprTnr.tpr !== null ? `${Math.round(selectedRunData.run.tprTnr.tpr * 100)}%` : '-'}
                                </div>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">TNR</span>
                                <div className={cn(
                                  'text-lg font-semibold',
                                  selectedRunData.run.tprTnr.tnr !== null && selectedRunData.run.tprTnr.tnr >= 0.85 ? 'text-green-600' :
                                  selectedRunData.run.tprTnr.tnr !== null && selectedRunData.run.tprTnr.tnr >= 0.7 ? 'text-yellow-600' : 'text-red-600'
                                )}>
                                  {selectedRunData.run.tprTnr.tnr !== null ? `${Math.round(selectedRunData.run.tprTnr.tnr * 100)}%` : '-'}
                                </div>
                              </div>
                            </div>
                            <div className="border-l pl-4">
                              <span className="text-xs text-muted-foreground block mb-1">Confusion Matrix</span>
                              <div className="grid grid-cols-2 gap-1 text-xs">
                                <div className="bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded text-center" title="True Positives">
                                  <span className="text-green-700 dark:text-green-400">TP: {selectedRunData.run.tprTnr.confusionMatrix.tp}</span>
                                </div>
                                <div className="bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded text-center" title="False Positives">
                                  <span className="text-red-700 dark:text-red-400">FP: {selectedRunData.run.tprTnr.confusionMatrix.fp}</span>
                                </div>
                                <div className="bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded text-center" title="False Negatives">
                                  <span className="text-red-700 dark:text-red-400">FN: {selectedRunData.run.tprTnr.confusionMatrix.fn}</span>
                                </div>
                                <div className="bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded text-center" title="True Negatives">
                                  <span className="text-green-700 dark:text-green-400">TN: {selectedRunData.run.tprTnr.confusionMatrix.tn}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {selectedRunLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : selectedRunData?.results && selectedRunData.results.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead className="w-12">Status</TableHead>
                          <TableHead>Query</TableHead>
                          <TableHead className="text-right w-20">Score</TableHead>
                          <TableHead className="w-20 text-center" title="Ground Truth Match">GT Match</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRunData.results.map((result) => (
                          <TestResultRow
                            key={result.id}
                            result={{
                              ...result,
                              passed: !!result.passed,
                              testCaseId: (result as { test_case_id?: string }).test_case_id || result.testCaseId,
                              executionId: (result as { execution_id?: string }).execution_id,
                              expectedResponse: (result as { expected_response?: string }).expected_response,
                              actualResponse: (result as { actual_response?: string }).actual_response,
                              overallScore: (result as { overall_score?: number }).overall_score,
                              scoresDetail: (result as { scores_detail?: Record<string, unknown> }).scores_detail,
                              errorMessage: (result as { error_message?: string }).error_message,
                              humanReviewed: !!(result as { human_reviewed?: number }).human_reviewed,
                              humanApproved: (result as { human_approved?: number }).human_approved === 1 ? true :
                                             (result as { human_approved?: number }).human_approved === 0 ? false : undefined,
                              humanReviewNotes: (result as { human_review_notes?: string }).human_review_notes,
                              testCaseExists: (result as { testCaseExists?: boolean }).testCaseExists,
                            }}
                            isExpanded={expandedResults.has(result.id)}
                            onToggle={() => toggleResultExpanded(result.id)}
                            onViewTrace={handleViewTrace}
                            onSubmitReview={(resultId, approved, notes) => humanReviewMutation.mutate({ resultId, approved, notes })}
                            isSubmittingReview={humanReviewMutation.isPending}
                            onRerunTestCase={(testCaseId, query) => rerunTestCaseMutation.mutate({ testCaseId, query })}
                            isRerunning={rerunTestCaseMutation.isPending}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No test results found
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Runs List */}
            {!selectedRunId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Evaluation Runs</CardTitle>
                  <CardDescription>History of batch evaluation runs. Click a run to see individual test results.</CardDescription>
                </CardHeader>
                <CardContent>
                  {runsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  ) : evalRuns?.runs && evalRuns.runs.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Tests</TableHead>
                          <TableHead className="text-right">Passed</TableHead>
                          <TableHead className="text-right">Avg Score</TableHead>
                          <TableHead className="text-right" title="True Positive Rate - % of ground truth passes correctly identified">TPR</TableHead>
                          <TableHead className="text-right" title="True Negative Rate - % of ground truth fails correctly identified">TNR</TableHead>
                          <TableHead className="text-right">Started</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {evalRuns.runs.map((run) => (
                          <TableRow key={run.id}>
                            <TableCell className="font-medium max-w-[250px]">
                              <div className="flex flex-col">
                                <span className="truncate" title={run.name || run.id}>{run.name || run.id.substring(0, 12)}</span>
                                <span className="text-xs text-gray-500 font-normal">
                                  {(() => {
                                    const date = parseUTCDate(run.startedAt);
                                    return date ? formatDistanceToNow(date, { addSuffix: true }) : '-';
                                  })()}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <EvalRunStatus status={run.status} />
                            </TableCell>
                            <TableCell className="text-right">{run.testCount}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                {run.passedCount}
                                {run.failedCount > 0 && (
                                  <>
                                    <XCircle className="h-3 w-3 text-red-500 ml-2" />
                                    {run.failedCount}
                                  </>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {run.avgScore ? `${Math.round(run.avgScore * 100)}%` : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {run.true_positive_rate != null ? (
                                <span className={cn(
                                  'font-medium',
                                  run.true_positive_rate >= 0.9 ? 'text-green-600' :
                                  run.true_positive_rate >= 0.7 ? 'text-yellow-600' : 'text-red-600'
                                )}>
                                  {Math.round(run.true_positive_rate * 100)}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {run.true_negative_rate != null ? (
                                <span className={cn(
                                  'font-medium',
                                  run.true_negative_rate >= 0.85 ? 'text-green-600' :
                                  run.true_negative_rate >= 0.7 ? 'text-yellow-600' : 'text-red-600'
                                )}>
                                  {Math.round(run.true_negative_rate * 100)}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {(() => {
                                const date = parseUTCDate(run.startedAt);
                                if (!date) return '-';
                                return (
                                  <span title={date.toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}>
                                    {formatDistanceToNow(date, { addSuffix: true })}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-right">
                              {run.status === 'completed' ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedRunId(run.id)}
                                >
                                  View Details
                                </Button>
                              ) : run.status === 'running' ? (
                                <div className="flex flex-col items-end gap-1 min-w-[180px]">
                                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                                    {(run.passedCount || 0) + (run.failedCount || 0) === 0 ? (
                                      <div className="absolute inset-0 bg-gradient-to-r from-blue-300 via-blue-500 to-blue-300 animate-pulse" />
                                    ) : (
                                      <div
                                        className="bg-blue-500 h-full transition-all duration-300 ease-out"
                                        style={{
                                          width: `${(run.testCount || 0) > 0 ? (((run.passedCount || 0) + (run.failedCount || 0)) / run.testCount) * 100 : 0}%`
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {(run.passedCount || 0) + (run.failedCount || 0)} / {run.testCount || 0} tests
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FlaskConical className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No evaluation runs yet</p>
                      <p className="text-xs mt-1">Click "Run Evaluation" to start</p>
                    </div>
                  )}
                  {/* Pagination */}
                  {!runsLoading && evalRunsTotalPages > 0 && (
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <span className="text-xs text-muted-foreground">
                        Page {evalRunsPage + 1} of {evalRunsTotalPages} ({evalRuns?.total} total)
                      </span>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEvalRunsPage(p => Math.max(0, p - 1))}
                          disabled={evalRunsPage === 0 || runsFetching}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEvalRunsPage(p => Math.min(evalRunsTotalPages - 1, p + 1))}
                          disabled={evalRunsPage >= evalRunsTotalPages - 1 || runsFetching}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Test Cases Tab */}
          <TabsContent value="test-cases">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm">Test Cases</CardTitle>
                  <CardDescription>
                    Golden test cases for evaluation. Label ground truth to enable TPR/TNR metrics.
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Test Case
                </Button>
              </CardHeader>
              <CardContent>
                {testCasesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : testCases?.testCases && testCases.testCases.length > 0 ? (
                  <>
                    {/* Summary stats */}
                    <div className="flex gap-4 mb-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">train</Badge>
                        <span className="text-muted-foreground">
                          {testCases.testCases.filter(tc => tc.dataset_split === 'train').length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">dev</Badge>
                        <span className="text-muted-foreground">
                          {testCases.testCases.filter(tc => tc.dataset_split === 'dev' || !tc.dataset_split).length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">test</Badge>
                        <span className="text-muted-foreground">
                          {testCases.testCases.filter(tc => tc.dataset_split === 'test').length}
                        </span>
                      </div>
                      <div className="border-l pl-4 flex items-center gap-1">
                        <span className="text-muted-foreground">Labeled:</span>
                        <span className="font-medium">
                          {testCases.testCases.filter(tc => tc.ground_truth_pass !== null && tc.ground_truth_pass !== undefined).length}
                        </span>
                        <span className="text-muted-foreground">/ {testCases.testCases.length}</span>
                      </div>
                    </div>
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Name</TableHead>
                          <TableHead className="w-[280px]">Query</TableHead>
                          <TableHead className="w-[70px]">Split</TableHead>
                          <TableHead className="w-[110px]">Ground Truth</TableHead>
                          <TableHead className="w-[100px]">Category</TableHead>
                          <TableHead className="w-[90px]">Difficulty</TableHead>
                          <TableHead className="w-[70px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {testCases.testCases.map((tc) => (
                          <TableRow key={tc.id}>
                            <TableCell className="font-medium truncate" title={tc.name}>{tc.name}</TableCell>
                            <TableCell className="truncate" title={tc.query}>
                              {tc.query}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn(
                                  tc.dataset_split === 'train' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  tc.dataset_split === 'test' ? 'bg-green-50 text-green-700 border-green-200' :
                                  'bg-amber-50 text-amber-700 border-amber-200'
                                )}
                              >
                                {tc.dataset_split || 'dev'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <GroundTruthLabel
                                testCase={tc}
                                onUpdate={(groundTruthPass, reasoning) => {
                                  updateTestCaseMutation.mutate({
                                    id: tc.id,
                                    groundTruthPass,
                                    groundTruthReasoning: reasoning,
                                  });
                                }}
                                isUpdating={updateTestCaseMutation.isPending}
                              />
                            </TableCell>
                            <TableCell>
                              {tc.category && <Badge variant="outline">{tc.category}</Badge>}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={tc.difficulty === 'hard' ? 'destructive' :
                                         tc.difficulty === 'medium' ? 'default' : 'secondary'}
                              >
                                {tc.difficulty}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    setEditingTestCase(tc);
                                    setEditFormData({
                                      name: tc.name,
                                      query: tc.query,
                                      description: tc.description || '',
                                      category: tc.category || '',
                                      difficulty: tc.difficulty,
                                      expectedResponse: '',
                                    });
                                    setShowEditDialog(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                  onClick={() => {
                                    if (confirm(`Delete test case "${tc.name}"?`)) {
                                      deleteTestCaseMutation.mutate(tc.id);
                                    }
                                  }}
                                  disabled={deleteTestCaseMutation.isPending}
                                >
                                  {deleteTestCaseMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No test cases found</p>
                    <p className="text-xs mt-1">Add test cases to start evaluating</p>
                    <Button
                      size="sm"
                      className="mt-4"
                      onClick={() => setShowCreateDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Test Case
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Evaluators Tab */}
          <TabsContent value="ai-evaluators" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5" />
                  Evaluation Architecture
                </CardTitle>
                <CardDescription>
                  Your evaluation system uses a three-tier architecture: deterministic rules, LLM judges, and human review.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Cpu className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100">Deterministic</h3>
                    </div>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Rule-based scoring for query expansion, retrieval quality, and formatting.
                    </p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="h-5 w-5 text-purple-600" />
                      <h3 className="font-semibold text-purple-900 dark:text-purple-100">LLM Judges</h3>
                    </div>
                    <p className="text-sm text-purple-800 dark:text-purple-200">
                      GPT-4o-mini evaluates faithfulness, hallucination, and user satisfaction.
                    </p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-5 w-5 text-amber-600" />
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100">Human Review</h3>
                    </div>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Admin approval/rejection with feedback that can train AI evaluators.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deterministic Scorers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Cpu className="h-4 w-4" />
                  Deterministic Scorers
                </CardTitle>
                <CardDescription>Rule-based evaluation without LLM calls</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Query Expander Scorer */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Query Expander Scorer</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Evaluates Phase 1 - query enrichment quality
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">search_term_quality</div>
                      <div className="font-bold text-blue-600">40%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">enrichment_quality</div>
                      <div className="font-bold text-blue-600">25%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">abbreviation_expansion</div>
                      <div className="font-bold text-blue-600">20%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">term_count</div>
                      <div className="font-bold text-blue-600">15%</div>
                    </div>
                  </div>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      View scoring rules
                    </summary>
                    <div className="mt-2 bg-muted p-3 rounded text-xs font-mono whitespace-pre-wrap">
{`Rules:
- Filters 60+ stop words (how, what, the, can, do, etc.)
- Minimum term length: 2 characters
- Ideal term count: 2-5 terms
- Checks abbreviation expansion in enriched query
- For follow-ups, verifies thread context incorporation`}
                    </div>
                  </details>
                </div>

                {/* Retrieval Scorer */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Retrieval Scorer</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Evaluates Phase 2 - vector search result quality
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">top_score_quality</div>
                      <div className="font-bold text-blue-600">30%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">score_distribution</div>
                      <div className="font-bold text-blue-600">20%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">relevance_threshold</div>
                      <div className="font-bold text-blue-600">20%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">result_count</div>
                      <div className="font-bold text-blue-600">15%</div>
                    </div>
                    <div className="bg-muted p-2 rounded text-center">
                      <div className="text-xs text-muted-foreground">source_diversity</div>
                      <div className="font-bold text-blue-600">15%</div>
                    </div>
                  </div>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      View scoring rules
                    </summary>
                    <div className="mt-2 bg-muted p-3 rounded text-xs font-mono whitespace-pre-wrap">
{`Thresholds:
- Minimum "good" score: 0.35 similarity
- "Excellent" score: 0.60 similarity
- Ideal result count: 5+ results
- Source diversity: 3+ different sources is ideal

Scoring:
- Top result quality based on similarity score
- Distribution measures consistency of top results
- Diversity rewards results from multiple sources`}
                    </div>
                  </details>
                </div>
              </CardContent>
            </Card>

            {/* LLM Judges - Dynamic with versioning */}
            <LLMJudgesSection />

            {/* AI Training Status */}
            <AITrainingStatus />

            {/* Learning from Human Feedback */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Training AI Evaluators
                </CardTitle>
                <CardDescription>How your human feedback improves AI evaluation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    When you approve or reject eval results with detailed feedback, this data can be used to:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border rounded-lg p-3">
                      <h4 className="font-medium text-sm mb-1">Few-Shot Learning</h4>
                      <p className="text-xs text-muted-foreground">
                        Include human-reviewed examples in LLM judge prompts to guide evaluation
                      </p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <h4 className="font-medium text-sm mb-1">Threshold Calibration</h4>
                      <p className="text-xs text-muted-foreground">
                        Adjust score thresholds based on human approval patterns
                      </p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <h4 className="font-medium text-sm mb-1">Pattern Analysis</h4>
                      <p className="text-xs text-muted-foreground">
                        Identify common failure modes from rejection categories
                      </p>
                    </div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-sm mb-2">Best Practices for Feedback</h4>
                    <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                      <li>Always select a rejection category when rejecting</li>
                      <li>Be specific in your notes - mention exactly what was wrong</li>
                      <li>For approvals, note what made the response particularly good</li>
                      <li>Review edge cases carefully - they teach the most</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Trace Viewer Modal */}
      <TraceViewer
        trace={selectedTrace}
        scores={traceScores || undefined}
        onClose={() => {
          setSelectedTrace(null);
          setTraceScores(null);
        }}
      />

      {/* Loading overlay for trace */}
      {traceLoading && (
        <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}

      {/* Create Test Case Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Test Case
            </DialogTitle>
            <DialogDescription>
              Create a new test case for evaluations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tc-name">Name *</Label>
              <Input
                id="tc-name"
                value={newTestCase.name}
                onChange={(e) => setNewTestCase(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Basic greeting test"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tc-query">Query *</Label>
              <Textarea
                id="tc-query"
                value={newTestCase.query}
                onChange={(e) => setNewTestCase(prev => ({ ...prev, query: e.target.value }))}
                placeholder="e.g., How do I reset my password?"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tc-description">Description</Label>
              <Textarea
                id="tc-description"
                value={newTestCase.description}
                onChange={(e) => setNewTestCase(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description of what this test case validates"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tc-expected">Expected Response</Label>
              <Textarea
                id="tc-expected"
                value={newTestCase.expectedResponse}
                onChange={(e) => setNewTestCase(prev => ({ ...prev, expectedResponse: e.target.value }))}
                placeholder="Optional expected response or key points"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tc-category">Category</Label>
                <Input
                  id="tc-category"
                  value={newTestCase.category}
                  onChange={(e) => setNewTestCase(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., FAQ, Onboarding"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tc-difficulty">Difficulty</Label>
                <Select
                  value={newTestCase.difficulty}
                  onValueChange={(value) =>
                    setNewTestCase(prev => ({ ...prev, difficulty: value as 'easy' | 'medium' | 'hard' }))
                  }
                >
                  <SelectTrigger id="tc-difficulty">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createTestCaseMutation.mutate(newTestCase)}
              disabled={!newTestCase.name || !newTestCase.query || createTestCaseMutation.isPending}
            >
              {createTestCaseMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Test Case
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Test Case Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Test Case
            </DialogTitle>
            <DialogDescription>
              Update the test case details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-tc-name">Name *</Label>
              <Input
                id="edit-tc-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Basic greeting test"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tc-query">Query *</Label>
              <Textarea
                id="edit-tc-query"
                value={editFormData.query}
                onChange={(e) => setEditFormData(prev => ({ ...prev, query: e.target.value }))}
                placeholder="e.g., How do I reset my password?"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tc-description">Description</Label>
              <Textarea
                id="edit-tc-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description of what this test case validates"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tc-category">Category</Label>
                <Input
                  id="edit-tc-category"
                  value={editFormData.category}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., FAQ, Onboarding"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-tc-difficulty">Difficulty</Label>
                <Select
                  value={editFormData.difficulty}
                  onValueChange={(value) =>
                    setEditFormData(prev => ({ ...prev, difficulty: value as 'easy' | 'medium' | 'hard' }))
                  }
                >
                  <SelectTrigger id="edit-tc-difficulty">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingTestCase) {
                  editTestCaseMutation.mutate({
                    id: editingTestCase.id,
                    ...editFormData,
                  });
                }
              }}
              disabled={!editFormData.name || !editFormData.query || editTestCaseMutation.isPending}
            >
              {editTestCaseMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
